// Central fetch wrapper: one place for base URL, JSON handling, auth cookies,
// timeouts, cancellation, and error normalization.
const BASE_URL = process.env.REACT_APP_API_URL
const DEFAULT_TIMEOUT_MS = 15000

/**
 * @param {string} endpoint  path appended to REACT_APP_API_URL
 * @param {object} [opts]
 * @param {string} [opts.method='GET']
 * @param {*}      [opts.body=null]      JSON-serialized when present
 * @param {object} [opts.headers={}]
 * @param {AbortSignal} [opts.signal]    caller cancellation (e.g. from useAsync)
 * @param {number} [opts.timeout=15000]  ms before the request auto-aborts
 * @returns {Promise<object>} parsed JSON on success; throws Error(message) otherwise
 */
export async function apiRequest (
  endpoint,
  { method = 'GET', body = null, headers = {}, signal, timeout = DEFAULT_TIMEOUT_MS } = {}
) {
  // Auto-abort on timeout; also chain the caller's signal if given.
  const controller = new AbortController()
  const timer = setTimeout(() => controller.abort(), timeout)
  if (signal) {
    if (signal.aborted) controller.abort()
    else signal.addEventListener('abort', () => controller.abort(), { once: true })
  }

  const options = {
    method,
    credentials: 'include',
    signal: controller.signal,
    headers: { 'Content-Type': 'application/json', ...headers }
  }
  if (body !== null && body !== undefined) {
    options.body = JSON.stringify(body)
  }

  const url = `${BASE_URL}${endpoint}`

  let response
  try {
    response = await fetch(url, options)
  } catch (error) {
    if (error.name === 'AbortError') {
      // Distinguish caller-cancellation from a timeout.
      throw new Error(
        signal && signal.aborted ? 'Request cancelled' : 'Request timed out. Please try again.'
      )
    }
    throw new Error('Cannot connect to the server. Please try again.')
  } finally {
    clearTimeout(timer)
  }

  const contentType = response.headers.get('content-type')
  let data

  if (contentType && contentType.includes('application/json')) {
    data = await response.json()
  } else {
    if (response.status === 503) {
      throw new Error('Backend server is unavailable. Please restart the Node.js application.')
    }
    throw new Error(`Server returned ${response.status} ${response.statusText}`)
  }

  if (!response.ok) {
    throw new Error(data.message || `Request failed with status ${response.status}`)
  }

  return data
}
